import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBed, faCouch } from '@fortawesome/free-solid-svg-icons';
import { RoomService } from 'src/app/services/room.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { BookingPersonService } from 'src/app/services/booking-person.service';
import { RoomModel } from 'src/app/models/room.model';
import { ReservationDetailModel } from 'src/app/models/reservation-detail.model';
import { DocumentTypeEnum } from 'src/app/shared/enumerators/document-type.enum';
import { BookingPersonModel } from 'src/app/models/booking-person.model';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateUtils } from 'src/app/shared/utils/date.util';
import { ReservationService } from 'src/app/services/reservation.service';
import { CountryModel } from 'src/app/models/country.model';
import { CountryService } from 'src/app/shared/services/country.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SwalFireService } from 'src/app/shared/services/swal-fire.service';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-booking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    NgxMatSelectSearchModule
  ],
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.css']
})
export class BookingComponent implements OnInit {
  faBed = faBed;
  faBedDouble = faCouch;

  countries: CountryModel[] = [];
  filteredCountries: CountryModel[] = [];
  countryFilterCtrl = new FormControl();
  destroy$ = new Subject<void>();

  reservationForm: FormGroup;

  totalNights: number = 0;
  rooms: any[] = [];
  inputOptions: Record<string, string> = {};
  selectedRooms: RoomModel[] = [];
  showRooms: boolean = false;
  totalPrice: number = 0;
  reservationId: number | null = null;

  reservationCreated: boolean = false;
  originalBookingPerson: BookingPersonModel | null = null;
  minDate: string;

  isUpdate = false;

  documentTypes = Object.keys(DocumentTypeEnum)
    .filter(key => !isNaN(Number(DocumentTypeEnum[key as any])))
    .map(key => ({
      value: DocumentTypeEnum[key as keyof typeof DocumentTypeEnum],
      label: key
    }));

  private lastSearchedValue: string | null = null;

  constructor(private roomSrv: RoomService,
    private toastr: ToastService,
    private bookingPersonSrv: BookingPersonService,
    private fb: FormBuilder,
    private reservationSrv: ReservationService,
    private router: Router,
    private countryService: CountryService,
    private readonly swalFire: SwalFireService,
    public activatedRoute: ActivatedRoute) {

    const today = new Date();
    today.setDate(today.getDate());
    this.minDate = today.toISOString().split('T')[0];

    this.reservationForm = this.fb.group({
      id: [0],
      checkInDate: ['', Validators.required],
      checkOutDate: ['', Validators.required],
      guestCount: [1, [Validators.required, Validators.min(1)]],
      bookingPersonId: [null],
      bookingPerson: this.fb.group({
        id: [0],
        reservationId: [null],
        documentType: [1, Validators.required],
        documentNumber: ['', Validators.required],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        areaCode: ['', [Validators.required, Validators.pattern(/^\+?[1-9]\d{1,13}$/)]],
        phoneNumber: ['', [Validators.required, Validators.pattern(/^(?!15)\d{6,10}$/)]],
        email: ['', [Validators.required, Validators.email]],
        countryOfOrigin: ['', Validators.required],
        originAddress: ['', Validators.required],
        isGuest: [true]
      }),
      totalPrice: [0, [Validators.required, Validators.min(0)]],
      observations: [''],
      reservationDetails: this.fb.array([]),
      createdDate: [null],
      createdBy: [null],
      modifiedDate: [null],
      modifiedBy: [null],
      status: [null],
    });

  }
  ngOnInit(): void {
    this.getRooms();
    this.countryService.getCountries().subscribe(data => {
      this.countries = data;
      this.filteredCountries = this.countries;
    });

    this.getReservation();
    this.countryFilterCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(search => {
        const term = search?.toLowerCase() || '';
        this.filteredCountries = this.countries.filter(country =>
          country.name.toLowerCase().includes(term)
        );
      });

    this.reservationForm.controls.checkOutDate.valueChanges.subscribe(val => {
      this.calculateNights();
      this.clean();
    });

    this.reservationForm.controls.checkInDate.valueChanges.subscribe(val => {
      this.calculateNights();
      this.clean();
    });

    this.reservationForm.controls.guestCount.valueChanges.subscribe(val => {
      this.clean();
    });

    this.reservationForm.get('bookingPerson.documentType')?.valueChanges.subscribe(() => {
      this.trySearchBookingPerson();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  parseToDate(input: string | Date): Date {
    if (input instanceof Date) {
      return input;
    }

    const parsed = new Date(input);

    if (isNaN(parsed.getTime())) {
      throw new Error("Invalid date format");
    }

    return parsed;
  }

  getReservation() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.reservationId = params['id'];
      if (this.reservationId) {
        this.reservationSrv.getById(this.reservationId).subscribe({
          next: (data) => {
            // this.minDate = data.checkInDate.toString().split('T')[0];
            data.checkInDate = this.parseToDate(data.checkInDate)
            data.checkOutDate = this.parseToDate(data.checkOutDate)
            this.reservationForm.patchValue(data);
            this.originalBookingPerson = { ...data.bookingPerson };
            data.reservationDetails.forEach(r => {
              r.room.nightPrice = r.priceAtBooking;
              this.addRoom(r.id, r.room);
            });
            this.searchRooms();
            this.isUpdate = true;
          },
          error: (err) => {
            this.toastr.error(
              'Reserva',
              'Error al obtener las reserva.'
            );
          }
        });
      }
    });
  }

  clean() {
    this.rooms = [];
    var count = this.reservationDetails.length;
    for (var i = 0; i < count; i++) {
      this.removeRoom(i);
    }
  }

  get reservationDetails(): FormArray {
    return this.reservationForm.get('reservationDetails') as FormArray;
  }

  // Método para agregar un detalle de habitación
  addRoom(detailId: number | null = 0, room: RoomModel): void {
    const roomDetail = this.fb.group({
      id: [detailId],
      roomId: [room.id, Validators.required],
      room: [room],  // Puede ser un objeto si se obtiene de un API
      priceAtBooking: [room.nightPrice, [Validators.required, Validators.min(0)]],
      numberOfNights: [this.totalNights, [Validators.required, Validators.min(1)]]
    });

    this.reservationDetails.push(roomDetail);
  }

  // Método para eliminar un detalle de habitación
  removeRoom(index: number): void {
    this.reservationDetails.removeAt(index);
  }

  calculateNights() {
    const start = this.reservationForm.controls.checkInDate.value;
    const end = this.reservationForm.controls.checkOutDate.value;
    if (start && end) {
      const diff = end.getTime() - start.getTime();
      this.totalNights = diff > 0 ? diff / (1000 * 60 * 60 * 24) : 0;
    }
  }

  getRooms() {
    this.roomSrv.get().subscribe({
      next: (data: RoomModel[]) => {
        const uniqueOptions = new Map<string, RoomModel>();

        data.forEach(room => {
          const key = `${room.capacity}-${room.nightPrice}`;
          if (!uniqueOptions.has(key)) {
            uniqueOptions.set(key, room);
          }
        });

        this.inputOptions = {};
        for (const room of uniqueOptions.values()) {
          const label = `Habitación para ${room.capacity} personas - $${room.nightPrice}`;
          this.inputOptions[room.nightPrice.toString()] = label;
        }
      },
    });
  }

  searchRooms() {
    this.showRooms = true;
    var checkInDate = this.reservationForm.controls.checkInDate.value;
    var checkOutDate = this.reservationForm.controls.checkOutDate.value;
    var reservationId = this.reservationForm.controls.id.value;

    if (!checkInDate || !checkOutDate) {
      alert('Por favor, selecciona un rango de fechas.');
      return;
    }

    this.roomSrv.getAvailableRooms(
      DateUtils.format(checkInDate, 'yyyy/MM/dd'),
      DateUtils.format(checkOutDate, 'yyyy/MM/dd'),
      reservationId)
      .subscribe({
        next: (data: RoomModel[]) => {
          if (data.length == 0) {
            this.toastr.error(
              'Habitaciones',
              'No hay habitaciones disponibles'
            );
          } else {
            data.forEach(room => {
              // Buscar si la room ya está seleccionada
              const existingDetail = this.reservationDetails.controls.find(d => d.value.roomId === room.id);
              if (existingDetail) {
                room.nightPrice = existingDetail.value.priceAtBooking;
              }
            });

            let total: number = data
              .reduce(function (a, b) {
                return a + b.capacity;
              }, 0);

            if (total < this.reservationForm.controls.guestCount.value) {
              this.toastr.error(
                'Habitaciones',
                'No se encontraron habitaciones disponibles para la cantidad de huéspedes en el rango de fechas seleccionado'
              );
            }
            else {
              this.rooms = data;
            }
          }
        },
        error: (err) => {
          this.toastr.error(
            'Habitaciones',
            'Error al obtener habitaciones'
          );
        }
      });
  }

  getList() {
    var details = this.reservationDetails.value;
    var rooms = [];

    details.forEach((d: ReservationDetailModel) => {
      if (d.room)
        rooms.push(d.room)
    });
    return rooms
  }


  toggleRoomSelection(room: any) {
    const index = this.reservationDetails.value.findIndex(r => r.roomId === room.id);
    if (index > -1) {
      this.removeRoom(index);
    } else {
      this.addRoom(0, room);
    }
    this.calculateTotal();
  }

  isRoomSelected(room: RoomModel): boolean {
    var test = this.getList().some(r => r.id === room.id);
    return test;
  }

  calculateTotal() {
    this.totalPrice = this.reservationDetails.value.reduce((sum, detail) => sum + detail.room.nightPrice * this.totalNights, 0);
  }

  trySearchBookingPerson() {
    const docType = this.reservationForm.get('bookingPerson.documentType')?.value;
    const docNumber = this.reservationForm.get('bookingPerson.documentNumber')?.value;
    const combinedKey = `${docType}-${docNumber}`;

    if (docType && docNumber && combinedKey !== this.lastSearchedValue) {
      this.lastSearchedValue = combinedKey;
      this.GetBookingPersonByDocNumber();
    }
  }

  GetBookingPersonByDocNumber() {

    var bp = (this.reservationForm.controls.bookingPerson as FormGroup).value as BookingPersonModel;

    var documentType = this.documentTypes.find(x => x.value == bp.documentType as DocumentTypeEnum).label
    this.reservationForm.controls.bookingPerson.disable();
    this.bookingPersonSrv.GetBookingPersonByDocNumber(bp.documentNumber, documentType).subscribe({
      next: (data: any) => {
        if (data.totalCount > 0) {
          var bp = data.items[0];

          if (this.isUpdate) {
            this.swalFire.SwalFireQuestion(
              "Responsable de la reserva",
              "¿Desea asociar esta persona a la reserva?",
              "Continuar",
              "Cancelar"
            ).then((result) => {
              if (result.isConfirmed) {
                this.setBookingPerson(bp);
              }
              else {
                this.rollbackBookingPerson();
              }
            });
          }
          else {
            this.setBookingPerson(bp);
          }
        } else {
          if (this.isUpdate) {
            this.swalFire.SwalFireQuestion(
              "Responsable de la reserva",
              "¿Desea asociar esta persona a la reserva?\nEsta persona no se encuentra en el sistema, debe ingresar su información.",
              "Continuar",
              "Cancelar"
            ).then((result) => {
              if (!result.isConfirmed) {
                this.rollbackBookingPerson();
              }
              else {
                // No se encontró: asociar persona nueva
                this.reservationForm.get('bookingPerson.firstName')?.reset();
                this.reservationForm.get('bookingPerson.lastName')?.reset();
                this.reservationForm.get('bookingPerson.areaCode')?.reset();
                this.reservationForm.get('bookingPerson.phoneNumber')?.reset();
                this.reservationForm.get('bookingPerson.email')?.reset();
                this.reservationForm.get('bookingPerson.countryOfOrigin')?.reset();
                this.reservationForm.get('bookingPerson.originAddress')?.reset();
                this.reservationForm.controls.bookingPersonId?.setValue(0);
                (this.reservationForm.controls.bookingPerson as FormGroup).controls.id.setValue(0);
              }

              this.reservationForm.controls.bookingPerson.enable();
            });
          }
          else {
            this.reservationForm.controls.bookingPerson.enable();
          }
        }
      }
    });
  }

  setBookingPerson(bp: BookingPersonModel) {
    this.reservationForm.controls.bookingPerson.patchValue(bp)
    this.reservationForm.controls.bookingPersonId.setValue(bp.id)
    this.reservationForm.controls.bookingPerson.enable();
  }

  rollbackBookingPerson() {
    this.reservationForm.controls.bookingPerson.patchValue(this.originalBookingPerson)
    this.reservationForm.controls.bookingPersonId.setValue(this.originalBookingPerson.id)
    const docType = this.reservationForm.get('bookingPerson.documentType')?.value;
    const docNumber = this.reservationForm.get('bookingPerson.documentNumber')?.value;
    this.lastSearchedValue = `${docType}-${docNumber}`;
    this.reservationForm.controls.bookingPerson.enable();
  }

  confirmReservation() {

    if (this.reservationForm.valid) {
      this.reservationSrv.post(this.reservationForm.value).subscribe({
        next: (data) => {
          this.toastr.success(
            'Reserva',
            'La reserva ha sido registrada exitosamente!!!'
          );
          this.swalFire.SwalFireQuestion(
            "Reserva",
            "¿Desea registrar pago para confirmar la Reserva?",
            "Si",
            "No"
          ).then((result) => {
            if (result.isConfirmed) {
              this.router.navigate([`payment`], { queryParams: { id: data.id } });
            }
            else {
              this.goToHome();
            }
          });
        },
        error: (err) => {
          this.toastr.error(
            'Reserva',
            'No se ha podido registrar la reserva, intentelo nuevamente.'
          );
        }
      });
    }
  }

  updateReservation() {

    if (this.reservationForm.valid) {
      this.reservationSrv.put(this.reservationForm.value).subscribe({
        next: (data) => {
          this.toastr.success(
            'Reserva',
            'La reserva ha sido actualizada exitosamente!!!'
          );
          this.goToHome();
        },
        error: (err) => {
          this.toastr.error(
            'Reserva',
            'No se ha podido actualizar la reserva, intentelo nuevamente.'
          );
        }
      });
    }
  }

  goToHome() {
    this.router.navigate(['/booking-list']);
  }

  openSelectDialog(event: MouseEvent, room: RoomModel): void {
    event.stopPropagation(); // para no disparar el toggle

    Swal.fire({
      title: 'Ingrese el precio que se le asignara a la habitacion',
      html: `<input id="newPrice" type="text" class="swal2-input my-custom-class" placeholder="Nuevo precio" inputmode="decimal" autocomplete="off" />`,
      showCancelButton: true,
      confirmButtonText: 'Actualizar',
      customClass: {
        popup: 'swal-custom-popup',
        input: 'swal-custom-select',
      },
      didOpen: () => {
        const input = document.getElementById('newPrice') as HTMLInputElement | null;
        const confirmBtn = Swal.getConfirmButton();

        if (!input) {
          confirmBtn.disabled = true;
          return;
        }

        const sanitizeValue = (raw: string): string => {
          let sanitized = raw.replace(/[^0-9.]/g, '');
          const dotIndex = sanitized.indexOf('.');

          if (dotIndex !== -1) {
            sanitized = sanitized.slice(0, dotIndex + 1) + sanitized.slice(dotIndex + 1).replace(/\./g, '');
          }

          const hasTrailingDot = sanitized.endsWith('.');
          let [integerPart = '', decimalPart = ''] = sanitized.split('.');

          if (integerPart.length > 1) {
            integerPart = integerPart.replace(/^0+/, '') || '0';
          }

          if (dotIndex === 0) {
            integerPart = '0';
          }

          decimalPart = decimalPart.slice(0, 2);

          if (decimalPart) {
            return `${integerPart}.${decimalPart}`;
          }

          return hasTrailingDot ? `${integerPart || '0'}.` : integerPart;
        };

        const validateInput = () => {
          const value = input.value.trim();
          const isValid = /^\d+(\.\d{0,2})?$/.test(value) && parseFloat(value) > 0;
          confirmBtn.disabled = !isValid;
        };

        input.addEventListener('input', () => {
          const sanitized = sanitizeValue(input.value);

          if (sanitized !== input.value) {
            input.value = sanitized;
          }

          validateInput();
        });

        input.addEventListener('blur', () => {
          const value = input.value.trim().replace(/\.$/, '');

          if (!value) {
            input.value = '';
            validateInput();
            return;
          }

          if (/^\d+(\.\d{0,2})?$/.test(value)) {
            input.value = (Math.round(parseFloat(value) * 100) / 100).toFixed(2);
          }

          validateInput();
        });

        input.focus();
        validateInput();
      }
    }).then(result => {
      if (result.isConfirmed) {
        const input = document.getElementById('newPrice') as HTMLInputElement | null;
        const newPrice = input ? parseFloat(input.value) : NaN;

        if (!input || isNaN(newPrice) || newPrice <= 0) {
          return;
        }

        const normalizedPrice = Math.round(newPrice * 100) / 100;

        // Actualiza el precio visible en la tarjeta
        room.nightPrice = normalizedPrice;

        // Actualiza tambien el precio en el formulario (priceAtBooking)
        const formControl = this.reservationDetails.controls.find(c => c.value.roomId === room.id);
        if (formControl) {
          formControl.patchValue({ priceAtBooking: normalizedPrice });
        }
      }
    });
  }
}
